import React from 'react';
import { Spinner } from 'react-bootstrap';
import { PatientService, ServicesContext } from '~/services';
import { enumValue, useServiceSubscribe } from '~/util';
import { AddNote } from './AddNote';
import { Note } from './Note';

interface NotesProps {
    addNote?: (note: NewNoteType) => void;
    canAdd?: boolean;
    entityId: number;
    enableOpenModal?: boolean;
    gap?: GapType | RiskGapType;
    includeActions?: boolean | string[];
    noteEntity?: string;
    noteType?: number;
    patient?: PatientType;
}
export function Notes({
    addNote,
    canAdd = true,
    entityId,
    enableOpenModal = false,
    gap,
    includeActions = false,
    noteEntity = 'patient.id',
    noteType = enumValue(['note', 'type', 'Patient Note']) as number,
    patient,
}: NotesProps) {
    const servicesContext = React.useContext(ServicesContext);
    const [loading, setLoading] = React.useState(false);
    const [noteOpen, setNoteOpen] = React.useState(false);
    const [notes, setNotes] = React.useState<NoteType[]>([]);

    const loadNotes = React.useCallback(async () => {
        setLoading(true);
        const result = await servicesContext.patient.getNotes(entityId, noteEntity, includeActions);
        setNotes(result);
        setLoading(false);
    }, [entityId, includeActions, noteEntity, servicesContext.patient]);

    useServiceSubscribe(
        noteType === enumValue(['note', 'type', 'Patient Note'])
            ? PatientService.EVENTS.notes_updated
            : null,
        loadNotes
    );

    React.useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    async function addNoteDefault(note: NewNoteType) {
        if (addNote) {
            await addNote(note);
        } else {
            await servicesContext.patient.update(entityId, note);
        }
    }

    async function createNewNote(newNote: string) {
        await addNoteDefault({
            notes: [
                {
                    note: newNote,
                    type: noteType,
                },
            ],
        });
        setNoteOpen(false);
        loadNotes();
    }

    return (
        <div>
            {canAdd && (
                <AddNote
                    noteOpen={noteOpen}
                    toggleNote={() => setNoteOpen((prev) => !prev)}
                    addNote={createNewNote}
                />
            )}

            <div className={noteOpen ? 'note-scroll-small' : 'note-scroll'}>
                {loading && (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                )}
                {!loading &&
                    notes.map((x) => (
                        <Note
                            key={x.id}
                            note={x}
                            entityId={entityId}
                            patient={patient}
                            gap={gap}
                            enableOpenModal={enableOpenModal}
                        />
                    ))}
            </div>
        </div>
    );
}
